import React, { useCallback, useMemo, useRef, useState } from 'react'

import {
  MinimapContainer,
  MinimapImage,
  MinimapImageWrapper,
  MinimapMultipageContainer,
  ViewportIndicator,
} from './documentMinimapStyles'
import { TransformState } from './types'

const MINIMAP_MAX_WIDTH = 192
const MINIMAP_MAX_HEIGHT = 144

interface DocumentMinimapProps {
  imageUrls: string[]
  transformState: TransformState
  imageNaturalDimensions: { w: number; h: number }
  wrapperDimensions: { w: number; h: number }
  onNavigate: (positionX: number, positionY: number, animated: boolean) => void
  isMultipage: boolean
}

export const DocumentMinimap: React.FC<DocumentMinimapProps> = ({
  imageUrls,
  transformState,
  imageNaturalDimensions,
  wrapperDimensions,
  onNavigate,
  isMultipage,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef<{
    startMouseX: number
    startMouseY: number
    startPosX: number
    startPosY: number
  } | null>(null)

  const { scale, positionX, positionY } = transformState
  const { w: imgW, h: imgH } = imageNaturalDimensions
  const { w: wrapperW, h: wrapperH } = wrapperDimensions

  const minimapScale = useMemo(() => {
    if (imgW === 0 || imgH === 0) return 1
    return Math.min(MINIMAP_MAX_WIDTH / imgW, MINIMAP_MAX_HEIGHT / imgH)
  }, [imgW, imgH])

  const minimapWidth = imgW * minimapScale
  const minimapHeight = imgH * minimapScale

  const viewportRect = useMemo(() => {
    if (scale === 0) return { x: 0, y: 0, w: minimapWidth, h: minimapHeight }
    return {
      x: (-positionX / scale) * minimapScale,
      y: (-positionY / scale) * minimapScale,
      w: (wrapperW / scale) * minimapScale,
      h: (wrapperH / scale) * minimapScale,
    }
  }, [
    scale,
    positionX,
    positionY,
    wrapperW,
    wrapperH,
    minimapScale,
    minimapWidth,
    minimapHeight,
  ])

  const clampedViewport = useMemo(() => {
    const x = Math.max(0, Math.min(viewportRect.x, minimapWidth))
    const y = Math.max(0, Math.min(viewportRect.y, minimapHeight))
    const w = Math.min(viewportRect.w, minimapWidth - x)
    const h = Math.min(viewportRect.h, minimapHeight - y)
    return { x, y, w, h }
  }, [viewportRect, minimapWidth, minimapHeight])

  const navigateToMinimapPoint = useCallback(
    (minimapX: number, minimapY: number, animated: boolean) => {
      const imgX = minimapX / minimapScale
      const imgY = minimapY / minimapScale
      const newPosX = wrapperW / 2 - imgX * scale
      const newPosY = wrapperH / 2 - imgY * scale
      onNavigate(newPosX, newPosY, animated)
    },
    [minimapScale, wrapperW, wrapperH, scale, onNavigate]
  )

  const handleMinimapClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) return
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      const minimapX = e.clientX - rect.left
      const minimapY = e.clientY - rect.top
      navigateToMinimapPoint(minimapX, minimapY, true)
    },
    [isDragging, navigateToMinimapPoint]
  )

  const handleViewportMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      setIsDragging(true)
      dragStartRef.current = {
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startPosX: positionX,
        startPosY: positionY,
      }

      const handleMouseMove = (moveEvt: MouseEvent) => {
        if (!dragStartRef.current) return
        const dx = moveEvt.clientX - dragStartRef.current.startMouseX
        const dy = moveEvt.clientY - dragStartRef.current.startMouseY
        const imgDx = dx / minimapScale
        const imgDy = dy / minimapScale
        const newPosX = dragStartRef.current.startPosX - imgDx * scale
        const newPosY = dragStartRef.current.startPosY - imgDy * scale
        onNavigate(newPosX, newPosY, false)
      }

      const handleMouseUp = () => {
        setIsDragging(false)
        dragStartRef.current = null
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [positionX, positionY, minimapScale, scale, onNavigate]
  )

  if (imgW === 0 || imgH === 0) return null

  return (
    <MinimapContainer ref={containerRef} onClick={handleMinimapClick}>
      <MinimapImageWrapper $width={minimapWidth} $height={minimapHeight}>
        {isMultipage ? (
          <MinimapMultipageContainer>
            {imageUrls.map((url, index) => (
              <MinimapImage
                key={index}
                src={url}
                alt={`minimap page ${index + 1}`}
                draggable={false}
              />
            ))}
          </MinimapMultipageContainer>
        ) : (
          <MinimapImage src={imageUrls[0]} alt='minimap' draggable={false} />
        )}
        <ViewportIndicator
          $left={clampedViewport.x}
          $top={clampedViewport.y}
          $width={clampedViewport.w}
          $height={clampedViewport.h}
          onMouseDown={handleViewportMouseDown}
        />
      </MinimapImageWrapper>
    </MinimapContainer>
  )
}
